import { axiosInstance } from './api';
import { API_BASE } from './base';

export interface SeatingPolygon {
  polygon_id: number;
  polygon_name: string | null;
  camera_id: number;
  camera_name: string | null;
  /** FE-104: average people present over all frames including zero-occupancy frames. */
  avg_occupancy: number;
  /** FE-114-B: average people present only during occupied frames (> 0 people). Always >= 1 when > 0. */
  avg_when_occupied: number;
  /** FE-104: hours/day the polygon had >= 1 person, normalized over `to-from` days. */
  occupied_hours_per_day: number;
  /** FE-104: company-size × minutes histogram. Keys are "1".."7" + "8+". */
  group_size_minutes: Record<string, number>;
  /** Legacy / debugging — sum of captured_occupancy across all frames. */
  total_occupancy: number;
  frame_count: number;
  is_table: boolean;
  is_rack: boolean;
  is_queue: boolean;
  is_staff_zone: boolean;
}

export interface SeatingStatsDto {
  zone_id: number;
  zone_name: string;
  from: string;
  to: string;
  polygons: SeatingPolygon[];
}

export interface TimeofdayDto {
  polygon_id: number;
  from: string;
  to: string;
  /** matrix[weekday_idx][hour] = avg_occupancy, weekday_idx 0=Mon..6=Sun */
  matrix: number[][];
}

export interface SeatingCompareDto {
  from: string;
  to: string;
  zone_a: { zone_id: number; zone_name: string; polygons: SeatingPolygon[] };
  zone_b: { zone_id: number; zone_name: string; polygons: SeatingPolygon[] };
}

/** Optional weekday filter:
 *  - 'all' (default): every day in [from, to].
 *  - 'weekend': only Saturdays + Sundays.
 *  - 'workday': only Mon-Fri.
 *  Used by the FE's "Last 8 weekend days" / "Last 10 workdays" period
 *  buttons; the FE picks a from/to range that contains exactly the
 *  requested count of matching days. */
export type SeatingWeekdays = 'all' | 'weekend' | 'workday';

export async function getSeatingStats(
  zoneId: string | number,
  from?: string,
  to?: string,
  weekdays: SeatingWeekdays = 'all',
): Promise<SeatingStatsDto> {
  const params: Record<string, string> = { zone_id: String(zoneId) };
  if (from) params.from = from;
  if (to) params.to = to;
  if (weekdays !== 'all') params.weekdays = weekdays;
  // No timeout. Cold compute on a 30-day window can run several
  // minutes when the per-day cache is empty (after a Redis wipe or
  // the very first 30-day call); a fixed timeout would axe the
  // request mid-flight while the BE is still happily computing.
  // The seating panel either returns fast (warm cache) or stays
  // pending; users get the visible spinner either way. If something
  // is genuinely hung, the user navigates / refreshes.
  const res = await axiosInstance.get<SeatingStatsDto>('/core/api/seating-stats/', {
    params,
    timeout: 0,
  });
  return res.data;
}

export async function getSeatingTimeofday(
  polygonId: number,
  from?: string,
  to?: string,
): Promise<TimeofdayDto> {
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await axiosInstance.get<TimeofdayDto>(
    `/core/api/seating-stats/${polygonId}/timeofday/`,
    { params },
  );
  return res.data;
}

export async function getSeatingCompare(
  zoneA: string | number,
  zoneB: string | number,
  from?: string,
  to?: string,
  weekdays: SeatingWeekdays = 'all',
): Promise<SeatingCompareDto> {
  const params: Record<string, string> = { zone_a: String(zoneA), zone_b: String(zoneB) };
  if (from) params.from = from;
  if (to) params.to = to;
  if (weekdays !== 'all') params.weekdays = weekdays;
  // No timeout — same rationale as getSeatingStats. Compare runs
  // the per-day compute for two zones, so cold-path duration is
  // ~2× a single-zone call. A fixed timeout would axe the request
  // while the BE is still computing on the second zone.
  const res = await axiosInstance.get<SeatingCompareDto>('/core/api/seating-stats/compare/', {
    params,
    timeout: 0,
  });
  return res.data;
}

/** Camera heatmap: bbox gradient overlaid on the camera's latest frame. */
export function cameraHeatmapUrl(
  cameraId: number,
  fromMs: number,
  toMs: number,
): string {
  return `${API_BASE}/core/api/camera-heatmap/?camera_id=${cameraId}&start_time=${fromMs}&end_time=${toMs}`;
}

export interface DensityGridDto {
  camera_id: number;
  /** URL of the camera's latest frame (use as background image). null when
   *  the camera has no Frame.image records yet — render bands on a dark
   *  placeholder instead of erroring. */
  image_url: string | null;
  /** Native camera resolution — use for SVG viewBox and object-contain math. */
  width: number;
  height: number;
  grid: {
    cols: number;
    rows: number;
    cell_w: number;
    cell_h: number;
    /** Row-major, length = cols × rows, values normalized 0..1 (8% floor applied server-side). */
    values: number[];
  };
  /** Mass-weighted average KDE bandwidth (in cells) at each cell. FE uses
   *  this to size the hover-dot's outer ring so it visualizes the local
   *  detection-cloud spread that produced the displayed value. */
  sigma_grid: {
    cols: number;
    rows: number;
    /** Row-major, length = cols × rows, σ in grid cells (1 dp). */
    values: number[];
  };
  /** Per-frame detection cells. Each entry is a flat array:
   *    [frame_id, gap_seconds, c1, r1, c2, r2, ...]
   *  giving the (column, row) of every detection in that frame. Empty
   *  frames (no detections) are omitted. The FE uses this to compute
   *  EXACT free-draw stats (occupied_hours_per_day + avg_when_occupied)
   *  the same way the seating endpoint computes them for annotated
   *  zones — counting frames where ≥1 detection's body-center falls
   *  inside the polygon. */
  frame_cells: number[][];
  /** Total online seconds across the window (Σ gap over all captured
   *  frames, gap-capped). Diagnostic only; FE does NOT divide by this
   *  for "h/day" math — that's done in actual-day units to match the
   *  seating endpoint scale. */
  total_online_seconds: number;
  /** Number of actual days in the requested window (= (end - start) /
   *  86400, min 1). Used as the denominator for hours/day numbers so a
   *  drawn polygon and an annotated zone of the same area report
   *  comparable values. */
  days_in_window: number;
  /** Per-cell h/day-actual that a body-center landed within ±1 cell
   *  (= 3×3 dilation of detection cells per frame, summed over frames).
   *  Decoupled from the KDE display grid: tight σ keeps visuals tight
   *  while this grid aggregates jitter so single-cell tooltip values
   *  approach the per-area / per-zone scale instead of a per-pixel
   *  fragment of it. */
  occupied_grid: {
    cols: number;
    rows: number;
    values: number[];
  };
  /** Max value in occupied_grid — for the colorbar peak label. */
  occupied_max: number;
  /** h/day floor below which cells render fully transparent. The FE
   *  uses this both to skip rendering and to label the colorbar's
   *  lower bound. Always populated in ellipse mode; absent (legacy)
   *  in radius mode — fall back to a hardcoded constant if missing. */
  density_cutoff?: number;
  /** Server-side hot-zone clusters. Each cluster ships its convex-hull
   *  polygon (normalized [0..1] image coords) plus stats so the FE
   *  renders a tooltip with the same shape as annotated zones. */
  hot_zones: HotZoneDto[];
  max_density: number;
  /** Per-day coverage diagnostic. The BE includes every day with any
   *  frames; status="partial" means coverage is below the threshold
   *  and the FE should surface a missing-data warning chip on top of
   *  the rendered heatmap. status="no_data" means the camera had no
   *  frames at all in the window — the FE renders an explanatory
   *  empty state instead. status="ok" is the regular case (no chip). */
  coverage?: {
    status: 'ok' | 'partial' | 'no_data';
    /** Days in the window with WH > 0 (filtered by weekday). */
    total_days: number;
    /** Days that have at least one recorded frame. */
    nonzero_days: number;
    /** Days at >= min_coverage_threshold (informational; not used
     *  for filtering, drives the "X of Y full days" line in the chip
     *  expansion). */
    kept_days: number;
    min_coverage_threshold: number;
    /** Working-hours seconds the camera was supposed to record over
     *  the period. */
    total_target_seconds: number;
    /** Working-hours seconds with frames recorded (capped at 60s
     *  per inter-frame gap, same metric used everywhere else). */
    total_online_seconds: number;
  };
}

export interface HotZoneDto {
  /** Convex hull, normalized [0..1] image coords. */
  polygon: number[][];
  /** Total person-hours/day inside the cluster (mass-conserving integral). */
  total_hours_per_day: number;
  /** Peak per-cell hours/day inside the cluster. */
  peak_hours_per_day: number;
  /** Approximate average people present when the cluster has anyone. */
  avg_when_occupied: number;
  /** Hours/day the cluster has at least one person (peak-cell upper bound). */
  occupied_hours_per_day: number;
  n_points: number;
}

/** Heatmap math mode.
 *  - 'ellipse' (default): simple bbox-ellipse rasterization. Returns
 *    grid + occupied_grid + max_density + density_cutoff. The empty
 *    fields (sigma_grid, frame_cells, hot_zones) are present for DTO
 *    compatibility but always empty in this mode.
 *  - 'radius' (legacy, superuser-only via UI): KDE σ + radius-based
 *    occupied_grid + per-frame detection cells + hot-zone clusters.
 *    Required for the free-draw and activity-zones overlays.
 *
 *  TODO 2026-08-06: drop 'radius' if no FE feature still needs it. */
export type HeatmapMode = 'ellipse' | 'radius';

export async function getCameraDensityGrid(
  cameraId: number,
  fromMs: number,
  toMs: number,
  mode: HeatmapMode = 'ellipse',
  weekdays: SeatingWeekdays = 'all',
): Promise<DensityGridDto> {
  // No timeout — the per-day progress bar gives the user visible
  // signal that work is happening, so we don't need a safety-net
  // timeout to surface "is it stuck?" anxiety. A fixed timeout
  // would just axe slow cold-path requests mid-flight while the
  // BE keeps computing; warm hits return fast anyway.
  // weekdays trims the BE per-day loop to only matching weekdays so a
  // "Last 8 weekend days" call computes 8 days, not the 28 calendar
  // days the window spans. Per-day cache is keyed by date alone so a
  // 30-day "all" call still warms cache for any weekday subset.
  const params: Record<string, string | number> = {
    camera_id: cameraId, start_time: fromMs, end_time: toMs, mode,
  };
  if (weekdays !== 'all') params.weekdays = weekdays;
  const res = await axiosInstance.get<DensityGridDto>('/core/api/camera-density-grid/', {
    params,
    timeout: 0,
  });
  return res.data;
}

/** Per-cell time-of-day breakdown — drives the click-tooltip's bar
 *  chart. Returns minutes/day at the given (col, row) cell, bucketed
 *  by day-of-week (Mon=0..Sun=6) and by zone-local hour (0..23). */
export interface CellTimeOfDayDto {
  camera_id: number;
  col: number;
  row: number;
  /** dow_minutes[0..6] = avg minutes/day on Mon..Sun. */
  dow_minutes: number[];
  /** hour_minutes[0..23] = avg minutes/day during that local hour,
   *  averaged across all days in the window. */
  hour_minutes: number[];
  /** dow_mask[0..6] — true when the zone has working hours on that
   *  weekday at any point in the window. FE renders only `true` bins
   *  so closed days don't dilute the bar chart with zero columns. */
  dow_mask?: boolean[];
  /** hour_mask[0..23] — true when the zone's working hours include
   *  any minute of that local hour at any point in the window. */
  hour_mask?: boolean[];
  total_minutes_per_day: number;
  days_in_window: number;
}

/** Per-day-loop progress — used by both heatmap and seating to render
 *  the same ETA + bar UX in their loader chips. Updated by the BE
 *  after each day; null when no compute is in flight or its TTL
 *  expired. The shape is fixed across endpoints (see
 *  services/api/progress_cache.py) so the FE polling hook can
 *  consume either endpoint with one implementation.
 *
 *  Renamed from HeatmapProgressDto on 2026-05-09 once seating
 *  joined the same contract; the old type alias stays for back-compat
 *  in case some caller still imports it. */
export interface DailyProgressDto {
  total_days: number;
  completed_days: number;
  cache_hits: number;
  cache_misses: number;
  started_at_ms: number;
  updated_at_ms: number;
  done: boolean;
}
export type HeatmapProgressDto = DailyProgressDto;

export async function getHeatmapProgress(
  cameraId: number,
  fromMs: number,
  toMs: number,
  mode: HeatmapMode = 'ellipse',
  weekdays: SeatingWeekdays = 'all',
): Promise<DailyProgressDto | null> {
  // Short timeout — this is a poll. If Redis or the BE is hung, fail
  // fast and the FE shows the bare spinner instead.
  // weekdays must match the main fetch's filter so we read the right
  // progress key — a 30d-all and 30d-weekend call write different
  // counters even when the camera + window are identical.
  try {
    const params: Record<string, string | number> = {
      camera_id: cameraId, start_time: fromMs, end_time: toMs, mode,
    };
    if (weekdays !== 'all') params.weekdays = weekdays;
    const res = await axiosInstance.get<DailyProgressDto | null>(
      '/core/api/camera-density-grid/progress/',
      { params, timeout: 5_000 },
    );
    return res.data ?? null;
  } catch {
    return null;
  }
}

/** Seating-stats compute progress. Same contract as heatmap progress;
 *  the FE shows an identical ETA + bar UX in the zones-mode loader
 *  chip. */
export async function getSeatingProgress(
  zoneId: string | number,
  from?: string,
  to?: string,
  weekdays: SeatingWeekdays = 'all',
): Promise<DailyProgressDto | null> {
  try {
    const params: Record<string, string> = { zone_id: String(zoneId) };
    if (from) params.from = from;
    if (to) params.to = to;
    if (weekdays !== 'all') params.weekdays = weekdays;
    const res = await axiosInstance.get<DailyProgressDto | null>(
      '/core/api/seating-stats/progress/',
      { params, timeout: 5_000 },
    );
    return res.data ?? null;
  } catch {
    return null;
  }
}

export async function getCellTimeOfDay(
  cameraId: number,
  col: number,
  row: number,
  fromMs: number,
  toMs: number,
  weekdays: SeatingWeekdays = 'all',
): Promise<CellTimeOfDayDto> {
  const params: Record<string, string | number> = {
    camera_id: cameraId,
    col, row,
    start_time: fromMs,
    end_time: toMs,
  };
  if (weekdays !== 'all') params.weekdays = weekdays;
  const res = await axiosInstance.get<CellTimeOfDayDto>(
    '/core/api/camera-density-grid/cell-timeofday/',
    {
      params,
      // Heavy first call; cached 24 h on the BE so subsequent clicks
      // on the same cell are instant.
      timeout: 60_000,
    },
  );
  return res.data;
}
